import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const STAT_CARDS = [
  { key: 'total', label: 'Total Tickets', color: '#7c3aed', icon: '🎫' },
  { key: 'open', label: 'Open', color: '#2563eb', icon: '📬' },
  { key: 'resolved', label: 'Resolved / Closed', color: '#059669', icon: '✅' },
  { key: 'overdue', label: 'Overdue (SLA)', color: '#dc2626', icon: '🚨' },
];

// Fallback palette for masters that have no color set.
const FALLBACK = ['#6366f1', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#64748b'];

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, t, st, pr] = await Promise.all([
          api.get('/tickets/stats'),
          api.get('/tickets'),
          api.get('/statuses').catch(() => ({ data: [] })),
          api.get('/priorities').catch(() => ({ data: [] })),
        ]);
        setStats(s.data);
        setRecent(t.data.slice(0, 6));
        setStatuses(st.data);
        setPriorities(pr.data);
      } catch (e) {
        // interceptor handles auth errors
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build ordered breakdown rows from a master list + a {name: count} map.
  const rows = (masters, countMap) => masters.map((m, i) => ({
    name: m.name,
    count: (countMap || {})[m.name] || 0,
    color: m.color || FALLBACK[i % FALLBACK.length],
  }));
  const statusRows = rows(statuses, stats.byStatus);
  const priorityRows = rows(priorities, stats.byPriority);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Dashboard</h1>
      <p style={{ color: '#64748b', marginBottom: 24 }}>Support ticket overview</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {STAT_CARDS.map(c => (
          <div key={c.key} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 28 }}>{c.icon}</div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{loading ? '—' : (stats[c.key] ?? 0)}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Breakdown title="Tickets by Status" rows={statusRows} loading={loading} />
        <Breakdown title="Tickets by Priority" rows={priorityRows} loading={loading} />
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Recent Tickets</h2>
          <Link to="/tickets" style={{ fontSize: 13, fontWeight: 500 }}>View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div style={{ color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>
            {loading ? 'Loading...' : 'No tickets yet.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr><th>Ticket</th><th>Subject</th><th>Priority</th><th>Status</th></tr>
            </thead>
            <tbody>
              {recent.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.ticketNumber || `#${t.id}`}</td>
                  <td>{t.subject}</td>
                  <td>{t.priority}</td>
                  <td>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Breakdown({ title, rows, loading }) {
  const total = rows.reduce((a, r) => a + r.count, 0);
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="card">
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{title}</h2>
      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading...</div>
      ) : total === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>No tickets yet.</div>
      ) : (
        rows.map(r => (
          <div key={r.name} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: '#334155' }}>{r.name}</span>
              <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.count}</span>
            </div>
            <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${(r.count / max) * 100}%`, height: '100%', background: r.color, borderRadius: 999, transition: 'width 0.3s' }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
}
